import { StrictMode, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { StytchProvider } from "@stytch/react";
import { createStytchClient } from "@stytch/react/headless";
import { HelmetProvider } from "react-helmet-async";
import { PostHogProvider } from "posthog-js/react";
import { BrowserRouter } from "react-router-dom";

import { App } from "./App";
import { env } from "@/config/env";
import {
  DevelopmentAuthBridge,
  StytchAuthBridge,
} from "@/components/auth/AuthProvider";
import "@/styles/main.scss";

const stytchClient = env.stytchPublicToken
  ? createStytchClient(env.stytchPublicToken, {
      customBaseUrl: env.stytchCustomBaseUrl,
    })
  : null;

function WithAuth({ children }: { children: ReactNode }) {
  if (!stytchClient) {
    if (!import.meta.env.DEV) {
      throw new Error("VITE_STYTCH_PUBLIC_TOKEN is required");
    }
    return <DevelopmentAuthBridge>{children}</DevelopmentAuthBridge>;
  }

  return (
    <StytchProvider stytch={stytchClient}>
      <StytchAuthBridge>{children}</StytchAuthBridge>
    </StytchProvider>
  );
}

function WithAnalytics({ children }: { children: ReactNode }) {
  if (!env.posthogKey) return <>{children}</>;

  return (
    <PostHogProvider
      apiKey={env.posthogKey}
      options={{ api_host: env.posthogHost }}
    >
      {children}
    </PostHogProvider>
  );
}

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Failed to find root element");

createRoot(rootElement).render(
  <StrictMode>
    <BrowserRouter>
      <WithAuth>
        <HelmetProvider>
          <WithAnalytics>
            <App />
          </WithAnalytics>
        </HelmetProvider>
      </WithAuth>
    </BrowserRouter>
  </StrictMode>,
);
